/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   salaryStructure.mapper.ts

   Bridges the form model and the backend CommonSalaryStructure entity.

   `structureType` is now a REAL persisted column, so it is sent on save and
   read back directly. For legacy rows saved before the column existed,
   inferStructureType() falls back to the populated field groups.

   • formToPayload — form values → the entity payload.
   • recordToForm  — a stored record → the form model (edit / view).

   Not persisted (no backend column, UI-only): trainee "insuranceApplicable".
   Everything else maps to a real column and round-trips.
   ──────────────────────────────────────────────────────────────────────── */
import { StructureType, templateForType, defaultsFor } from "./salaryStructure.config";

const n = (v: any): number | null => {
  if (v === "" || v === null || v === undefined) return null;
  const parsed = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(parsed) ? Number(parsed) : null;
};

// Every template-specific column defaulted to null; we overlay the active
// template's values so inactive-template fields are explicitly cleared.
const NULL_TEMPLATE_FIELDS: Record<string, any> = {
  basicSalaryPercentage: null,
  hraPercentage: null,
  halfYearBonusPercentage: null,
  performanceBonusPercentage: null,
  annualBonusPercentage: null,
  annualBonusIsPercentage: null,
  conveyanceAllowance: null,
  conveyanceIsPercentage: null,
  medicalAllowance: null,
  medicalAllowanceIsPercentage: null,
  medicalInsuranceMonthly: null,
  medicalInsuranceIsPercentage: null,
  leaDays: null,
  leaMonthly: null,
  leaIsPercentage: null,
  pfApplicable: null,
  pfWageCeilingApplicable: null,
  providentFundEmployeePercentage: null,
  providentFundEmployerPercentage: null,
  esiApplicable: null,
  esiEmployerPercentage: null,
  esiEmployeePercentage: null,
  profTaxApplicable: null,
  noidaPtApplicable: null,
  taxOnProfession: null,
  gstRequired: null,
  gstContractValue: null,
  gstMonthlyPayout: null,
  gstTdsRate: null,
  gstPercentage: null,
  nonGstContractValue: null,
  nonGstMonthlyPayout: null,
  nonGstTdsRate: null,
  partTimeContractValue: null,
  partTimeMonthlyPayout: null,
  partTimePeriod: null,
};

const mapEmployee = (v: any): Record<string, any> => ({
  basicSalaryPercentage: n(v.basicSalaryPercentage),
  hraPercentage: n(v.hraPercentage),
  halfYearBonusPercentage: n(v.halfYearBonusPercentage),
  performanceBonusPercentage: n(v.performanceBonusPercentage),
  annualBonusPercentage: n(v.annualBonusPercentage),
  annualBonusIsPercentage: v.annualBonusIsPercentage ?? true,
  conveyanceAllowance: n(v.conveyanceAllowance),
  conveyanceIsPercentage: Boolean(v.conveyanceIsPercentage),
  medicalAllowance: n(v.medicalAllowance),
  medicalAllowanceIsPercentage: Boolean(v.medicalAllowanceIsPercentage),
  // Insurance is a benefit toggle; the amount only matters when applicable.
  medicalInsuranceMonthly: v.medicalInsuranceApplicable ? n(v.medicalInsuranceMonthly) : null,
  medicalInsuranceIsPercentage: Boolean(v.medicalInsuranceIsPercentage),
  leaDays: n(v.leaDays),
  leaMonthly: n(v.leaMonthly),
  leaIsPercentage: Boolean(v.leaIsPercentage),
  pfApplicable: Boolean(v.pfApplicable),
  pfWageCeilingApplicable: Boolean(v.pfWageCeilingApplicable),
  providentFundEmployeePercentage: n(v.providentFundEmployeePercentage),
  providentFundEmployerPercentage: n(v.providentFundEmployerPercentage),
  esiApplicable: Boolean(v.esiApplicable),
  esiEmployerPercentage: n(v.esiEmployerPercentage),
  esiEmployeePercentage: n(v.esiEmployeePercentage),
  profTaxApplicable: Boolean(v.profTaxApplicable),
  noidaPtApplicable: Boolean(v.noidaPtApplicable),
  taxOnProfession: v.profTaxApplicable ? n(v.taxOnProfession) : 0,
});

const mapContract = (v: any): Record<string, any> => {
  const contractValue = n(v.contractValue);
  const monthlyPayout = n(v.monthlyPayout);
  const tdsRate = n(v.tdsRate);
  if (v.contractType === "GST") {
    return {
      gstRequired: true,
      gstContractValue: contractValue,
      gstMonthlyPayout: monthlyPayout,
      gstTdsRate: tdsRate,
      gstPercentage: n(v.gstPercentage),
      nonGstContractValue: null,
      nonGstMonthlyPayout: null,
      nonGstTdsRate: null,
    };
  }
  return {
    gstRequired: false,
    nonGstContractValue: contractValue,
    nonGstMonthlyPayout: monthlyPayout,
    nonGstTdsRate: tdsRate,
    gstContractValue: null,
    gstMonthlyPayout: null,
    gstTdsRate: null,
    gstPercentage: null,
  };
};

const mapTrainee = (v: any): Record<string, any> => {
  const stipend = n(v.monthlyStipend) ?? 0;
  const months = n(v.periodMonths) ?? 0;
  return {
    partTimeContractValue: stipend * months,
    partTimeMonthlyPayout: stipend,
    partTimePeriod: months ? `${months} months` : null,
    // PF / ESI booleans have real backend homes and round-trip.
    pfApplicable: Boolean(v.pfApplicable),
    esiApplicable: Boolean(v.esiApplicable),
  };
};

/**
 * Form values → entity payload. On edit we spread the original record first so
 * server-managed / unedited fields (id, gratuityApplicable, fbtPercentage,
 * variableAllPercentage, …) survive.
 */
export const formToPayload = (values: any, initialRecord?: any): Record<string, any> => {
  const type: StructureType = values.structureType;
  const template = templateForType(type);
  let mapped: Record<string, any> = {};
  if (template === "EMPLOYEE") mapped = mapEmployee(values);
  else if (template === "CONTRACT") mapped = mapContract(values);
  else mapped = mapTrainee(values);

  const base = initialRecord ? { ...initialRecord } : {};
  return {
    ...base,
    ...NULL_TEMPLATE_FIELDS,
    ...mapped,
    structureType: type,
    structureName: (values.structureName ?? "").trim(),
    description: values.description ?? "",
  };
};

const VALID_TYPES: StructureType[] = [
  "PERMANENT_EMPLOYEE",
  "CONTRACT_EMPLOYEE",
  "TRAINEE",
  "INTERN",
  "CONSULTANT",
];

/**
 * Read the persisted structureType. For legacy rows without it, fall back to
 * inferring the template from which field groups are populated.
 */
export const inferStructureType = (record: any): StructureType => {
  if (!record) return "PERMANENT_EMPLOYEE";
  if (VALID_TYPES.includes(record.structureType)) return record.structureType;

  if (record.partTimeContractValue != null || record.partTimeMonthlyPayout != null || record.partTimePeriod) {
    return "TRAINEE";
  }
  if (
    record.gstContractValue != null ||
    record.nonGstContractValue != null ||
    record.gstRequired === true ||
    record.gstMonthlyPayout != null ||
    record.nonGstMonthlyPayout != null
  ) {
    return "CONTRACT_EMPLOYEE";
  }
  return "PERMANENT_EMPLOYEE";
};

const parsePeriodMonths = (period: any): number | undefined => {
  if (period == null) return undefined;
  const m = String(period).match(/\d+/);
  return m ? parseInt(m[0], 10) : undefined;
};

/** Stored record → form model for edit/view. */
export const recordToForm = (record: any): Record<string, any> => {
  const type = inferStructureType(record);
  const template = templateForType(type);
  const defaults = defaultsFor(type);
  const common = {
    structureType: type,
    structureName: record?.structureName ?? "",
    description: record?.description ?? "",
  };

  if (template === "EMPLOYEE") {
    return {
      ...defaults,
      ...common,
      basicSalaryPercentage: record.basicSalaryPercentage ?? defaults.basicSalaryPercentage,
      hraPercentage: record.hraPercentage ?? defaults.hraPercentage,
      halfYearBonusPercentage: record.halfYearBonusPercentage ?? 0,
      performanceBonusPercentage: record.performanceBonusPercentage ?? 0,
      annualBonusPercentage: record.annualBonusPercentage ?? undefined,
      annualBonusIsPercentage: record.annualBonusIsPercentage ?? true,
      conveyanceAllowance: record.conveyanceAllowance ?? undefined,
      conveyanceIsPercentage: record.conveyanceIsPercentage ?? false,
      medicalAllowance: record.medicalAllowance ?? undefined,
      medicalAllowanceIsPercentage: record.medicalAllowanceIsPercentage ?? false,
      leaDays: record.leaDays ?? undefined,
      leaMonthly: record.leaMonthly ?? undefined,
      leaIsPercentage: record.leaIsPercentage ?? false,
      medicalInsuranceApplicable: record.medicalInsuranceMonthly != null,
      medicalInsuranceMonthly: record.medicalInsuranceMonthly ?? undefined,
      medicalInsuranceIsPercentage: record.medicalInsuranceIsPercentage ?? false,
      pfApplicable: record.pfApplicable ?? false,
      pfWageCeilingApplicable: record.pfWageCeilingApplicable ?? false,
      providentFundEmployeePercentage: record.providentFundEmployeePercentage ?? 12,
      providentFundEmployerPercentage: record.providentFundEmployerPercentage ?? 12,
      esiApplicable: record.esiApplicable ?? false,
      esiEmployerPercentage: record.esiEmployerPercentage ?? 3.25,
      esiEmployeePercentage: record.esiEmployeePercentage ?? 0.75,
      profTaxApplicable: record.profTaxApplicable ?? false,
      taxOnProfession: record.taxOnProfession ?? 0,
      noidaPtApplicable: record.noidaPtApplicable ?? false,
    };
  }

  if (template === "CONTRACT") {
    const gst = record.gstRequired === true;
    return {
      ...defaults,
      ...common,
      contractType: gst ? "GST" : "NON_GST",
      contractValue: (gst ? record.gstContractValue : record.nonGstContractValue) ?? undefined,
      monthlyPayout: (gst ? record.gstMonthlyPayout : record.nonGstMonthlyPayout) ?? undefined,
      tdsRate: (gst ? record.gstTdsRate : record.nonGstTdsRate) ?? undefined,
      gstPercentage: record.gstPercentage ?? defaults.gstPercentage,
    };
  }

  // TRAINEE / INTERN
  return {
    ...defaults,
    ...common,
    monthlyStipend: record.partTimeMonthlyPayout ?? undefined,
    periodMonths: parsePeriodMonths(record.partTimePeriod),
    pfApplicable: record.pfApplicable ?? false,
    esiApplicable: record.esiApplicable ?? false,
    insuranceApplicable: record.medicalInsuranceMonthly != null,
  };
};
